import React, { useState } from "react";
import {
  DefaultButton,
  DetailsList,
  Icon,
  ImageIcon,
  Panel,
  PanelType,
  PrimaryButton,
  Stack,
  Text,
  TextField,
} from "@fluentui/react";
import { IIngestionDefinition, IStacCollection } from "types/stac";
import { useIngestionsWithRuns } from "utils/requests";
import {
  IngestionIdColumn,
  IngestionNameColumn,
} from "components/controls/IngestionColumnDefinition";
import res from "../../../pages/CollectionDetail/assets/All-Resources.svg";

// Define the props for DeletePanel
interface DeletePanelProps {
  collection: IStacCollection;
  onDismiss: () => void;
  onConfirm: () => void;
}

const DeletePanel: React.FC<DeletePanelProps> = ({
  collection,
  onDismiss,
  onConfirm,
}) => {
  // Define the state for the collection name
  const [collectionName] = useState<string>(collection.title || collection.id);

  const handleDelete = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onDismiss();
  };

  const body = (
    <Text>
      The following collection and all its dependent ingestions and runs will be
      permanently deleted.
    </Text>
  );

  const [currentPage] = useState<number>(0);
  const [pageSize] = useState<number>(20);
  const { data: ingestionsResult } = useIngestionsWithRuns(
    collection.id,
    currentPage,
    pageSize
  );
  const ingestionCount = ingestionsResult?.length || 0;

  // Define the table for the connections
  const connectionsTable = (
    <DetailsList
      items={ingestionsResult || []}
      columns={[
        IngestionNameColumn,
        IngestionIdColumn,
        {
          key: "runs",
          name: "Associated Runs",
          fieldName: "ingestionRuns",
          minWidth: 140,
          isResizable: true,
          onRender: (item: IIngestionDefinition) => {
            return item.ingestionRuns?.length; // Assuming ingestionRuns is an array
          },
        },
      ]}
      selectionMode={0}
      layoutMode={0}
      isHeaderVisible={true}
      selectionPreservedOnEmptyClick={true}
      enterModalSelectionOnTouch={true}
    />
  );

  const [deleteEnabled, setDeletedEnabled] = useState(false);
  const handleTextChange = (
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    if (newValue === "delete") {
      setDeletedEnabled(true);
    } else {
      setDeletedEnabled(false);
    }
  };

  const itemList = (
    <Stack style={{ display: "flex" }}>
      <br />
      <Text
        style={{
          height: "20px",
          fontSize: "15px",
          fontWeight: 600,
          lineHeight: "20px",
        }}
      >
        Dependent ingestions and runs to be deleted ({ingestionCount})
      </Text>
      {connectionsTable}
    </Stack>
  );
  return (
    <Panel
      isOpen={true}
      type={PanelType.medium}
      onDismiss={handleCancel}
      headerText="Delete collection"
      isFooterAtBottom={true}
      onRenderFooterContent={() => (
        <div>
          <PrimaryButton
            style={deleteEnabled ? enabledButton : disabledButton}
            onClick={handleDelete}
            disabled={!!!deleteEnabled}
            ariaLabel="Confirm"
          >
            Delete
          </PrimaryButton>
          <DefaultButton onClick={handleCancel}>Cancel</DefaultButton>
        </div>
      )}
    >
      <div
        style={{
          display: "flex",
          height: "100%",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{ flex: 1 }}>
          {body}

          <br />
          <br />
          <Text style={{ fontSize: 16, fontWeight: "bold" }}>
            Collection to be deleted
          </Text>
          <Stack style={resource} horizontal>
            <ImageIcon style={allResourcesInstance} imageProps={{ src: res }} />
            <Text style={label2}>{collectionName}</Text>
            <Icon iconName="copy" />
          </Stack>
          <br />
          {itemList}
        </div>
        <div>
          <TextField
            required
            id="deleteTextField"
            label="Enter [delete] to confirm deletion"
            onChange={handleTextChange}
            ariaLabel="deleteTextField"
          />
        </div>
      </div>
    </Panel>
  );
};

export default DeletePanel;

const resource = {
  display: "flex",
  flexDirection: "row",
  gap: "10px",
  alignItems: "center",
  justifyContent: "flex-start",
  alignSelf: "stretch",
  flexShrink: 0,
  height: "32px",
  position: "relative",
} as React.CSSProperties;

const allResourcesInstance = {
  flexShrink: "0 !important",
  width: "22px !important",
  height: "22px !important",
} as React.CSSProperties;

const label2 = {
  color: "var(--text-body, #292827)",
  textAlign: "left",
  fontFamily: 'var(--az-text-body-font-family, "SegoeUi-Regular", sans-serif)',
  fontSize: "var(--az-text-body-font-size, 13px)",
  lineHeight: "var(--az-text-body-line-height, 18px)",
  fontWeight: "var(--az-text-body-font-weight, 400)",
  position: "relative",
} as React.CSSProperties;

const disabledButton = {
  background: "var(--az-palette-disabled, rgb(128, 128, 128, 0.1))",
  color: "var(--text-body, rgb(161, 159, 157))",
  border: "none",
  fill: "var(--text-body, rgb(161, 159, 157))",
  marginRight: "8px",
} as React.CSSProperties;

const enabledButton = {
  background: "var(--az-palette-red, rgb(195, 39, 39)",
  color: "var(--az-palette-white,rgb(255, 255, 255))",
  border: "1px solid var(--az-palette-red, rgb(195, 39, 39)",
  marginRight: "8px",
} as React.CSSProperties;
